import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ReportPreviewFrame } from '@/components/patientReports/ReportPreviewFrame';
import { useClinic } from '@/hooks/useClinicSettings';
import { usePatientReportSettings } from '@/hooks/usePatientReports';
import { loadReportAuthorName } from '@/lib/patientReports/docxFile';
import type { PatientReportLetterheadChoice } from '@/lib/patientReports/model';
import type { PlaceholderFillContext } from '@/lib/patientReports/placeholders';
import { materializeReportPdf } from '@/lib/patientReports/reportDocument';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

/**
 * PDF preview of the filled Word template.
 * The cover sheet is in front. Letterhead is the chrome on the report pages.
 */
export function WordReportPreview({
  clinicId,
  wordPath,
  coverPath,
  coverMime,
  includeCover,
  letterhead,
  context,
  blocks,
  patientId,
  canRevealCpr,
  sampleCpr,
  fallback,
  sourceRevision = 0,
}: {
  clinicId: string | null;
  wordPath: string;
  coverPath: string | null;
  coverMime: string | null;
  includeCover: boolean;
  letterhead: PatientReportLetterheadChoice;
  context: PlaceholderFillContext;
  blocks: readonly { heading: string; body: string }[];
  patientId: string | null;
  canRevealCpr: boolean;
  sampleCpr?: string | null;
  fallback: ReactNode;
  /** Bumps when the file at wordPath is replaced. The path itself stays the same. */
  sourceRevision?: number;
}) {
  const { t } = useTranslation();
  const settings = usePatientReportSettings(clinicId);
  const clinic = useClinic(clinicId);
  const [generatedAt] = useState(() => new Date());
  const [pdf, setPdf] = useState<Uint8Array | null>(null);
  const [pending, setPending] = useState(true);
  const [failed, setFailed] = useState(false);
  const inputRef = useRef({ context, blocks });
  inputRef.current = { context, blocks };

  const revision = useMemo(
    () =>
      JSON.stringify({
        wordPath,
        coverPath,
        coverMime,
        includeCover,
        letterhead,
        patientId,
        canRevealCpr,
        sampleCpr: sampleCpr ?? null,
        title: context.reportTitle,
        coverTitle: context.coverTitle,
        coverBody: context.coverBody,
        cpr: sampleCpr != null ? null : context.cpr,
        cprAccess: context.cprAccess,
        patient: context.patient?.id ?? null,
        name: context.patient?.name ?? '',
        journal: context.journal,
        markings: context.markings,
        questionnaire: context.questionnaire,
        files: context.files,
        text: context.text,
        content: context.content,
        toc: context.toc,
        marks: (context.contentPieces ?? [])
          .map((piece) =>
            piece.type === 'heading'
              ? piece.style
              : piece.type === 'toc'
                ? `${piece.headingStyle ?? 'bold'}:${piece.entries.map((entry) => entry.style ?? 'bold').join(',')}`
                : '',
          )
          .join('|'),
        blocks,
        sourceRevision,
      }),
    [
      blocks,
      canRevealCpr,
      context,
      coverMime,
      coverPath,
      includeCover,
      letterhead,
      patientId,
      sampleCpr,
      sourceRevision,
      wordPath,
    ],
  );

  useEffect(() => {
    if (!clinicId) {
      setFailed(true);
      setPending(false);
      return;
    }
    if (!settings.data) return;
    const settingsRow = settings.data;
    const clinicRow = clinic.data;
    let cancelled = false;
    setPending(true);
    const timer = window.setTimeout(() => {
      const { context: placeholders, blocks: blockSnapshot } = inputRef.current;
      void (async () => {
        try {
          const authorName = await loadReportAuthorName();
          const bytes = await materializeReportPdf({
            settings: settingsRow,
            clinic: clinicRow,
            letterhead,
            wordPath,
            coverPath,
            coverMime,
            includeCover,
            placeholders: {
              ...placeholders,
              authorName,
              clinicName: clinicRow?.name ?? '',
              generatedAt,
            },
            title: placeholders.reportTitle,
            blocks: blockSnapshot,
            patientId,
            canRevealCpr,
            sampleCpr,
          });
          if (cancelled) return;
          setPdf(bytes);
          setFailed(false);
          setPending(false);
        } catch {
          if (cancelled) return;
          setPdf(null);
          setFailed(true);
          setPending(false);
        }
      })();
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    canRevealCpr,
    clinic.data,
    clinicId,
    coverMime,
    coverPath,
    generatedAt,
    includeCover,
    letterhead,
    patientId,
    revision,
    sampleCpr,
    settings.data,
    wordPath,
  ]);

  const url = useMemo(() => {
    if (!pdf) return null;
    const copy = new Uint8Array(pdf.byteLength);
    copy.set(pdf);
    return URL.createObjectURL(new Blob([copy], { type: 'application/pdf' }));
  }, [pdf]);

  useEffect(() => {
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [url]);

  return (
    <section aria-label={t('patientReports.preview')}>
      <h3 className={cn(TYPE.section, 'text-ds-text')}>{t('patientReports.preview')}</h3>
      <p className={cn(TYPE.small, 'mt-s1 text-ds-muted')}>
        {t('patientReports.preview_word_hint')}
      </p>
      {pending ? (
        <p className={cn(TYPE.small, 'mt-s3 text-ds-muted')}>
          {t('patientReports.preview_word_loading')}
        </p>
      ) : null}
      {failed ? (
        <p className={cn(TYPE.small, 'mt-s3 text-ds-red-ink')}>
          {t('patientReports.preview_word_error')}
        </p>
      ) : null}
      {url ? (
        <ReportPreviewFrame
          className="mt-s4"
          title={t('patientReports.preview')}
          src={url}
          iframeTestId="report-pdf-preview"
        />
      ) : null}
      {failed ? <div className="mt-s4">{fallback}</div> : null}
    </section>
  );
}
